/**
 * RecentPagesWidget — "What's new" feed of recently updated CM pages.
 *
 * Sprint CM-5 closure (deferred from original CM-5 scope; shipped in
 * the post-CM-13 close-out pass). The widget is registered in
 * manifest.json under the same slug ("recent-pages") so the dashboard
 * registry recognises it.
 *
 * Cross-References:
 *   - manifest.json (cm.widgets[0])
 *   - sprint-cm-5-navigation.md (What's New deliverable)
 */
import { useEffect, useState, type FC } from 'react';
import { Clock, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import type { WidgetProps } from '@/types';

interface PageItem {
  id: string;
  title: string;
  slug: string;
  status: string;
  site_id: string | null;
  updated_at: string;
  published_at: string | null;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const day = 24 * 60 * 60 * 1000;
  const diff = Date.now() - then;
  if (diff < day) return 'today';
  if (diff < 2 * day) return 'yesterday';
  const days = Math.floor(diff / day);
  if (days < 14) return `${days}d ago`;
  if (days < 60) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

const RecentPagesWidget: FC<WidgetProps> = (_props) => {
  const [pages, setPages] = useState<PageItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch<PageItem[]>('/mod/cm/pages?status=published')
      .then((data) => {
        if (cancelled) return;
        const sorted = (data || [])
          .slice()
          .sort(
            (a, b) =>
              new Date(b.updated_at).getTime() -
              new Date(a.updated_at).getTime(),
          )
          .slice(0, 8);
        setPages(sorted);
      })
      .catch(() => !cancelled && setPages([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <p>Loading recent updates…</p>;
  if (pages.length === 0) {
    return (
      <p style={{ color: 'var(--color-text-muted)' }}>
        No published pages yet.
      </p>
    );
  }

  return (
    <ul className="recent-pages-widget">
      {pages.map((p) => (
        <li key={p.id} className="recent-pages-widget__item">
          <Link to={`/modules/cm/pages/${p.id}`} className="recent-pages-widget__link">
            <FileText size={14} className="recent-pages-widget__icon" />
            <span className="recent-pages-widget__title">{p.title}</span>
          </Link>
          <span className="recent-pages-widget__when">
            <Clock size={11} /> {relativeTime(p.updated_at)}
          </span>
        </li>
      ))}
    </ul>
  );
};

export default RecentPagesWidget;
